/**
 * Signed revocation objects — inert, content-addressed, verifiable by anyone.
 *
 * A revocation states one fact: the token or anchor with this hash is revoked.
 * It is signed by a key that could have granted what it revokes, so trust lives
 * in the object rather than in the channel it arrived on. That is what makes it
 * safe to accept from a stranger: revocation is monotone, so a verified
 * revocation can only reduce access, never widen it, and lying in the harmful
 * direction is stopped by the signature.
 *
 * Like an anchor, a revocation authenticates nothing. Publishing one is free.
 *
 * Design: projects/docs/2026/08/21/signed-revocation-objects.md. Phases 1 and 2
 * are here: the format and its authority rule, verification, the durable store,
 * and `createStoredRevocationChecker`, which fetches published objects from a
 * domain and is composed into the chain verifier. Peer-to-peer delivery is
 * Phase 3 — nothing here sends or accepts one over a peer channel yet.
 */

import { jose, localforage } from '../deps.js'
import { watchStore } from '../acequia/storageWatchdog.js'
import {
    hashToken,
    resolveLinkLocally,
    createLinkResolver,
    revocationSourceUnreachable,
    resolveChain,
    MAX_CHAIN_LENGTH,
} from './chains.js'

/** JWS `typ` that marks an object as a revocation. Refused as a bearer. */
export const REVOCATION_TYP = 'revocation'

// Durable store of verified revocations, keyed by the hash they revoke.
//
// Sibling to `acequia-chain-links`, and permanent for the same reason anchors
// are: a revocation is immutable and terminal, so there is no invalidation and
// no freshness window. Unlike the link cache, this one is never collected —
// forgetting a revocation restores access.
let revocationStore
if (typeof window !== 'undefined' || typeof self !== 'undefined') {
    try {
        revocationStore = watchStore(localforage.createInstance({ name: 'acequia-revocations' }), 'acequia-revocations')
    } catch {
        // Non-browser context — verification still works, storage does not.
    }
}

// Revocations are a few hundred bytes. This only stops a malformed or hostile
// object from filling storage.
const MAX_REVOCATION_BYTES = 16 * 1024

const HEX_64 = /^[0-9a-f]{64}$/

/**
 * Mint a signed revocation.
 *
 * The signer is supplied rather than taken from ambient identity, because both
 * signers in the model call this: a domain server holding its own key, and a
 * delegation holder revoking inside its own subtree.
 *
 * @param {Object} params
 * @param {string} params.revokes - SHA-256 hex of the anchor or leaf being revoked
 * @param {CryptoKey|Object} params.signingKey - private key (jose KeyLike)
 * @param {string} params.kid - the signer's key id, as it appears in the chain
 * @param {string} [params.sub] - the signer's userId, for key resolution
 * @param {string} [params.reason] - free-form, mirrors the server list's field
 * @param {string} [params.revokedAt] - ISO timestamp; defaults to now
 * @param {string} [params.authority] - hash of the ancestor the signer claims
 *   authority through. Informational: verification recomputes it and only
 *   cross-checks this against what the walk found.
 * @returns {Promise<string>} compact JWS
 */
export async function createRevocation({
    revokes,
    signingKey,
    kid,
    sub,
    reason,
    revokedAt,
    authority,
} = {}) {
    if (!HEX_64.test(revokes || '')) {
        throw new Error('revokes must be a SHA-256 hex hash')
    }
    if (!signingKey) throw new Error('signingKey required')
    if (!kid) throw new Error('kid required')

    const payload = {
        revokes,
        revokedAt: revokedAt || new Date().toISOString(),
        ...(reason ? { reason } : {}),
        ...(authority ? { authority } : {}),
    }
    const jws = new jose.SignJWT(payload)
        .setProtectedHeader({ alg: 'PS256', kid, typ: REVOCATION_TYP })
        .setIssuedAt()
    if (sub) jws.setSubject(sub)
    return jws.sign(signingKey)
}

/**
 * Recompute the ancestry of a revoked hash: the node itself, then every anchor
 * above it, root-ward.
 *
 * Walking upward is what implements "you cannot revoke upward" — a descendant's
 * signer never appears in this list, so it can never authorize a revocation of
 * something above it. Nothing here reads a claimed lineage; the walk is the
 * only source of one.
 *
 * @returns {Promise<{nodes?: Array<{hash, kid, sub}>, error?: string}>}
 */
async function walkAuthority(revokedHash, resolveLink) {
    const nodes = []
    let currentHash = revokedHash

    while (currentHash !== undefined && currentHash !== null) {
        let jwt = null
        try {
            jwt = await resolveLink(currentHash)
        } catch {
            jwt = null // a failing resolver is a miss, not an error
        }
        if (!jwt) {
            return { error: `Cannot resolve ${currentHash} — authority is unverifiable` }
        }
        // Re-derived, never trusted from the key it was asked for by. Same rule
        // resolveLinkLocally applies to the link cache, for the same reason.
        if ((await hashToken(jwt)) !== currentHash) {
            return { error: `Resolved bytes do not hash to ${currentHash}` }
        }

        let payload, header
        try {
            payload = jose.decodeJwt(jwt)
            header = jose.decodeProtectedHeader(jwt)
        } catch {
            return { error: `Invalid JWT at ${currentHash}` }
        }

        nodes.push({ hash: currentHash, kid: header.kid, sub: payload.sub })

        if (nodes.length > MAX_CHAIN_LENGTH) {
            return { error: 'Ancestry exceeds maximum depth' }
        }

        // A leaf hangs off an anchor; an anchor hangs off its parent.
        currentHash = payload.anchor !== undefined ? payload.anchor : payload.parent
    }

    return { nodes }
}

/**
 * Verify a revocation: it is well-formed, its signer had authority over what it
 * revokes, and the signature holds.
 *
 * Authority is the delegation rule read backwards — you may revoke what you
 * could have granted. A signer qualifies if its key signed the revoked node or
 * any ancestor of it, or if it is the domain's server key, which is accepted as
 * a signer at any depth and so is an ancestor of everything in its domain.
 *
 * A delegate's authority comes from the walk, so a delegate-signed revocation is
 * refused when the ancestry cannot be recomputed.
 *
 * A server key gets one exception, and only where it is sound: `origin:
 * 'domain'` means we fetched this object from the domain whose key signed it,
 * so the domain is answering about its own revocation list and the ancestry is
 * not what confers its standing. That matters because **a revocation outlives
 * what it revokes** — the list is permanent while anchors are collected, leaf
 * hashes are never published and legacy links expire, so the oldest and most
 * consequential revocations name nothing resolvable. Requiring a walk there
 * refused all of them, which is how this was found: against real data.
 *
 * `origin: 'peer'`, the default, gets no exception. Someone handing you an
 * object proves nothing about whether its signer had standing, and hashes are a
 * global namespace — so a server signature could otherwise name a chain in a
 * domain it has nothing to do with. Two BrowserDAV instances on one domain,
 * rooted at different paths, are already two authorities and neither may revoke
 * the other's grants; "same domain" is far too coarse a boundary to express
 * that. Peer-delivered objects are therefore judged by the one rule the whole
 * model rests on: you may revoke what you could have granted.
 *
 * `serverKeyAuthority: false` declines the exception even for domain-sourced
 * objects.
 *
 * @param {string} jws - compact JWS
 * @param {Object} options
 * @param {Function} options.resolvePublicKey - async (sub, kid) => { publicKey: JWK, status } | null
 * @param {Function} [options.resolveLink] - async (hash) => jwt|null; defaults to local-only
 * @param {boolean} [options.serverKeyAuthority=true] - honour `server-*` as an ancestor for domain-sourced objects
 * @param {'peer'|'domain'} [options.origin='peer'] - where this object came
 *   from. 'domain' asserts it was fetched from the domain whose key signed it;
 *   the default assumes it was not, because that is the assumption that is safe
 *   to be wrong about.
 * @returns {Promise<{valid: boolean, error?: string, revokes?, kid?, authorityHash?, payload?}>}
 */
export async function verifyRevocation(jws, options = {}) {
    const {
        resolvePublicKey,
        resolveLink = resolveLinkLocally,
        serverKeyAuthority = true,
        origin = 'peer',
    } = options
    if (!resolvePublicKey) {
        return { valid: false, code: 'BAD_CALL', error: 'resolvePublicKey callback is required' }
    }
    if (typeof jws !== 'string' || jws.length > MAX_REVOCATION_BYTES) {
        return { valid: false, code: 'BAD_FORMAT', error: 'Revocation is not a string of plausible size' }
    }

    let header, payload
    try {
        header = jose.decodeProtectedHeader(jws)
        payload = jose.decodeJwt(jws)
    } catch {
        return { valid: false, code: 'BAD_FORMAT', error: 'Revocation is not a valid JWS' }
    }
    if (header.typ !== REVOCATION_TYP) {
        return { valid: false, code: 'NOT_REVOCATION', error: `Not a revocation (typ ${header.typ})` }
    }
    if (!header.kid) {
        return { valid: false, code: 'BAD_FORMAT', error: 'Revocation has no kid' }
    }
    if (!HEX_64.test(payload.revokes || '')) {
        return { valid: false, code: 'BAD_FORMAT', error: 'Revocation names no valid hash' }
    }

    // A server key is treated as an ancestor of everything in its domain ONLY
    // when this object came from that domain — when we asked its endpoint and it
    // answered about its own revocation list. Then the shortcut is sound and
    // costs nothing: the domain is the party with standing, and we heard it
    // directly.
    //
    // Handed one by a peer, that shortcut is exactly the hole. Hashes are a
    // global namespace, so "signed by some domain's server key" says nothing
    // about whether that domain had any standing over the chain being killed.
    // Two BrowserDAV instances on one domain, rooted at different paths, are
    // already two authorities; neither may revoke the other's grants. So a
    // peer-delivered object gets no exception at all — the server key is judged
    // like any other signer, by whether it actually appears in the recomputed
    // ancestry. Which is the whole model in one sentence: you may revoke what
    // you could have granted.
    const serverSigned =
        serverKeyAuthority && origin === 'domain' && header.kid.startsWith('server-')

    // Skip a walk that cannot change the answer. For a domain-sourced server
    // signature the ancestry is not what confers authority, so unless the object
    // claims a specific authority to check against, walking tells us nothing —
    // and offline it is a doomed network attempt on every read, since this runs
    // from `isRevoked` on every chain hop.
    const needWalk = !serverSigned || !!payload.authority
    const { nodes, error: walkError } = needWalk
        ? await walkAuthority(payload.revokes, resolveLink)
        : { nodes: null }
    const match = nodes ? nodes.find((n) => n.kid === header.kid) : null

    // The signature is checked BEFORE authority, and independently of it: it is
    // the only question here that needs nothing but the signer's own key. That
    // separation is what lets a reader tell "these bytes were tampered with"
    // apart from "I cannot reach the chain right now" — one is a permanent fact
    // about the object, the other is a passing fact about the network.
    const keyInfo = await resolvePublicKey(match ? match.sub : payload.sub, header.kid)
    if (!keyInfo) {
        return { valid: false, code: 'KEY_NOT_FOUND', error: `Revocation signer key not found: ${header.kid}` }
    }
    if (keyInfo.status === 'revoked') {
        return { valid: false, code: 'KEY_REVOKED', error: `Revocation signer key revoked: ${header.kid}` }
    }

    let publicKey
    try {
        publicKey = await jose.importJWK(keyInfo.publicKey, 'PS256')
    } catch {
        return { valid: false, code: 'BAD_KEY', error: 'Invalid revocation signer public key' }
    }
    try {
        await jose.jwtVerify(jws, publicKey)
    } catch (err) {
        return { valid: false, code: 'BAD_SIGNATURE', error: `Revocation signature invalid: ${err.message}` }
    }

    // Authentic bytes whose ancestry could not be recomputed.
    //
    // This is the normal case, not the exceptional one: a revocation outlives
    // what it revokes. The list is permanent while anchors are collected, leaf
    // hashes are never published, and legacy chain links expire — so most
    // revocations name something no longer resolvable anywhere. Requiring the
    // walk here made precisely the oldest and most important revocations
    // unverifiable, which was found by pointing this at real data.
    //
    // For a delegate, the walk is the only evidence of standing, so its absence
    // is still fatal. Reported as its own outcome rather than folded into
    // failure: a stored revocation must not be discarded because the network is
    // down, or because the thing it killed was cleaned up afterwards.
    // A server key's authority does not come from the walk — it is a universal
    // ancestor within its domain — so it is the one signer that can still be
    // judged when the walk finds nothing.
    if (!nodes && !serverSigned) {
        return { valid: false, code: 'UNRESOLVABLE', signatureValid: true, error: walkError }
    }
    if (!nodes) {
        return {
            valid: true,
            signatureValid: true,
            authorityUnresolved: true,
            revokes: payload.revokes,
            kid: header.kid,
            authorityHash: null,
            payload,
        }
    }

    // The signer must be one of the nodes it could have granted from.
    if (!match && !serverSigned) {
        return {
            valid: false,
            code: 'NO_AUTHORITY',
            signatureValid: true,
            error: `Signer ${header.kid} has no authority over ${payload.revokes}`,
        }
    }

    // A stated `authority` is cross-checked against the recomputed walk, never
    // read in place of it. Claiming an ancestor you are not under is a refusal,
    // not a hint to follow.
    if (payload.authority && !nodes.some((n) => n.hash === payload.authority)) {
        return {
            valid: false,
            code: 'AUTHORITY_MISMATCH',
            signatureValid: true,
            error: `Claimed authority ${payload.authority} is not an ancestor of ${payload.revokes}`,
        }
    }

    return {
        valid: true,
        signatureValid: true,
        revokes: payload.revokes,
        kid: header.kid,
        authorityHash: match ? match.hash : null,
        payload,
    }
}

/**
 * Author a revocation with your own key and publish it to the domain.
 *
 * The domain can always mint one itself — but it signs with the server key, so
 * the published object records that access ended without recording who ended
 * it. The revoke routes already know: they gate on descent, and `auditActor`
 * writes the actor down as a string nobody can check. Signing it yourself is
 * what turns that note into proof.
 *
 * Authority is the same rule as everywhere else, and the server re-derives it
 * rather than taking this at face value: you may revoke what you could have
 * granted, so your key must appear in the recomputed ancestry of what you are
 * revoking. Publishing also revokes — the domain's list stays the source of
 * truth, so the object and `check` can never disagree.
 *
 * @param {Object} params
 * @param {string} params.authUrl
 * @param {string} params.authToken - bearer for the PUT
 * @param {string} params.revokes - hash of the anchor or leaf to revoke
 * @param {CryptoKey|Object} params.signingKey - the actor's private key
 * @param {string} params.kid - the actor's key id, as it appears in the chain
 * @param {string} [params.sub] - the actor's userId
 * @param {string} [params.reason]
 * @returns {Promise<{published: boolean, revoked?: boolean, jws: string, error?: string}>}
 */
export async function publishAuthoredRevocation({
    authUrl = '/auth',
    authToken,
    revokes,
    signingKey,
    kid,
    sub,
    reason = 'authored-revocation',
} = {}) {
    const jws = await createRevocation({ revokes, signingKey, kid, sub, reason })

    const resp = await fetch(`${authUrl}/revocations/${encodeURIComponent(revokes)}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/jwt',
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: jws,
    })

    if (!resp.ok) {
        let message = `Publish failed: ${resp.status}`
        try {
            message = (await resp.json()).message || message
        } catch { /* keep the status */ }
        return { published: false, jws, error: message }
    }

    const body = await resp.json()

    // Keep our own copy too. We minted it, so it verifies here by definition,
    // and storing it means this peer stops asking the domain about a hash it
    // just killed itself.
    await persist(revokes, jws)

    return { published: true, revoked: !!body.revoked, jws }
}

/**
 * Verify a revocation and keep it.
 *
 * Keyed by the hash it revokes, because that is the only question anyone asks
 * of this store. The object's own content address names it but nothing looks it
 * up that way, so using it as the key would be shape for its own sake.
 *
 * @returns {Promise<{stored: boolean, revokes?: string, error?: string}>}
 */
export async function storeRevocation(jws, options = {}) {
    const result = await verifyRevocation(jws, options)
    if (!result.valid) return { stored: false, error: result.error }
    return persist(result.revokes, jws)
}

/** Write a revocation already known to verify. */
async function persist(revokes, jws) {
    if (!revocationStore) return { stored: false, error: 'No revocation store' }
    if (typeof jws !== 'string' || jws.length > MAX_REVOCATION_BYTES) {
        return { stored: false, error: 'Revocation is not a string of plausible size' }
    }
    try {
        await revocationStore.setItem(revokes, jws)
    } catch (err) {
        return { stored: false, error: `Could not store revocation: ${err.message}` }
    }
    return { stored: true, revokes }
}

/**
 * Read a stored revocation, re-verifying it.
 *
 * The store is not part of the trust boundary — a content address relates bytes
 * to a name and says nothing about authority, so the signature is checked on
 * every read rather than once on write. This is the one place the design
 * deliberately does not copy the link cache, whose hash check on read is
 * sufficient only because a link is verified again in the chain walk anyway.
 *
 * Only a *permanent* failure drops the entry — bad signature, wrong typ, filed
 * under a hash it does not name. An entry whose ancestry merely cannot be
 * resolved right now is kept and still counts: authority was proved once, at
 * write time, and it cannot expire. Dropping on an unresolvable walk would make
 * an offline peer forget a revocation permanently, which restores access — the
 * one thing this design says must never happen.
 *
 * @returns {Promise<string|null>} the compact JWS, or null
 */
export async function loadRevocation(revokedHash, options = {}) {
    if (!revocationStore || !HEX_64.test(revokedHash || '')) return null

    let jws
    try {
        jws = await revocationStore.getItem(revokedHash)
    } catch {
        return null
    }
    if (!jws) return null

    const result = await verifyRevocation(jws, options)

    const drop = async () => {
        try {
            await revocationStore.removeItem(revokedHash)
        } catch {
            // Best-effort: the caller is getting null either way.
        }
        return null
    }

    // Filing is checkable with no network at all, and a mismatch is permanent.
    let names = null
    try {
        names = jose.decodeJwt(jws).revokes
    } catch {
        names = null
    }
    if (names !== revokedHash) return drop()

    if (result.valid) return jws

    // Drop only on a failure that proves these bytes were never a valid
    // revocation. Everything else — a key we cannot resolve, an ancestry we
    // cannot walk — is a fact about right now rather than about the object, and
    // must not cost us the revocation. An allowlist, not a denylist: a code
    // added later should default to keeping, because the failure mode of
    // keeping too much is a wasted read and the failure mode of dropping too
    // much is restored access.
    return PERMANENT_FAILURES.has(result.code) ? drop() : jws
}

/** Verification outcomes that are properties of the bytes, not of the moment. */
const PERMANENT_FAILURES = new Set([
    'BAD_FORMAT',
    'NOT_REVOCATION',
    'BAD_SIGNATURE',
])

/**
 * Is this hash revoked according to what we hold locally?
 *
 * The read half of the store, shaped like the `isRevoked(hash)` callback the
 * chain verifier takes. Composing it into that verifier is Phase 2 — nothing
 * calls this yet.
 */
export async function isRevokedLocally(revokedHash, options = {}) {
    return (await loadRevocation(revokedHash, options)) !== null
}

/**
 * A revocation checker backed by verified objects that survive a reload.
 *
 * This is what replaces `revokedForever`, the in-memory set that cached
 * "revoked" answers permanently and then died with the tab. Same monotone idea,
 * made durable and made checkable: what is kept is a signed object, so the
 * answer no longer depends on trusting the connection that delivered it.
 *
 * Shaped as the `isRevoked(hash)` callback `resolveAndVerify` already takes, so
 * it composes into `createCombinedRevocationChecker` beside the wallet and
 * server checkers. Composition is OR over a monotone property — another checker
 * can only make the answer more correct.
 *
 * Offline it answers `false`. That is the decided posture, not an oversight:
 * the server is a rendezvous, not the source of truth, and failing closed would
 * undo offline verification. The difference from before is that a revocation
 * this peer has *already seen* now answers `true` offline, which is the whole
 * point of the phase.
 *
 * @param {Object} options
 * @param {string} [options.authUrl='/auth']
 * @param {Function} options.resolvePublicKey
 * @param {Function} [options.resolveLink] - defaults to local-first-then-network
 * @param {boolean} [options.serverKeyAuthority]
 * @returns {Function} async (tokenHash) => boolean
 */
/**
 * How long a "not revoked" answer from a domain is reused (2026-10-21, owner's
 * choice): a revocation made elsewhere (the dashboard, another browser) takes
 * effect at a verifier within a minute; one made in this browser at once (the
 * wallet, and the store above). Without it every request a camera served cost
 * the domain a lookup per hash — 29% of acequia.io's requests on 2026-10-20.
 */
export const CLEAN_TTL_MS = 60 * 1000
const CLEAN_MAX = 5000

// Clean answers by domain, shared by every checker in this page: verifyGrant
// makes a checker per call, so a cache inside one would last one request
const _clean = new Map() // authUrl → Map(hash → epoch ms the answer expires)

function cleanFor(authUrl) {
    let m = _clean.get(authUrl)
    if (!m) _clean.set(authUrl, (m = new Map()))
    return m
}

function markClean(clean, hash) {
    if (clean.size >= CLEAN_MAX) {
        const now = Date.now()
        for (const [h, until] of clean) if (until <= now) clean.delete(h)
        if (clean.size >= CLEAN_MAX) clean.delete(clean.keys().next().value)
    }
    clean.set(hash, Date.now() + CLEAN_TTL_MS)
}

/** Forget the clean answers (tests; a page that must see a revocation now). */
export function forgetCleanRevocationAnswers() {
    _clean.clear()
}

export function createStoredRevocationChecker({
    authUrl = '/auth',
    resolvePublicKey,
    resolveLink,
    serverKeyAuthority,
} = {}) {
    const verifyOptions = {
        resolvePublicKey,
        resolveLink: resolveLink || createLinkResolver(authUrl),
        // Everything this checker sees was fetched from `authUrl` — both the
        // objects it pulls and, by construction, the ones it stored earlier.
        // Peer-delivered objects arrive through a different door (Phase 3) and
        // must not borrow this one's provenance.
        origin: 'domain',
        ...(serverKeyAuthority === undefined ? {} : { serverKeyAuthority }),
    }

    const clean = cleanFor(authUrl)

    async function isRevoked(tokenHash, audit) {
        if (!HEX_64.test(tokenHash || '')) return false

        // What we already hold. Durable, and true offline — the reason this
        // checker exists, and the reason an unreachable domain below is a
        // smaller loss than it used to be.
        if (await isRevokedLocally(tokenHash, verifyOptions)) return true

        // The domain said "not revoked" within the last minute
        const until = clean.get(tokenHash)
        if (until && Date.now() < until) return false

        // Otherwise ask the domain for the object — never for a verdict. The
        // bytes are checked here before they count for anything, so a hostile
        // or mistaken answer cannot revoke someone by assertion.
        let resp
        try {
            resp = await fetch(`${authUrl}/revocations/${encodeURIComponent(tokenHash)}`)
        } catch {
            return revocationSourceUnreachable(audit)
        }
        // 404 is an answer: this domain holds no revocation for that hash. Any
        // other failure is a source we did not reach.
        if (resp.status === 404) {
            markClean(clean, tokenHash)
            return false
        }
        if (!resp.ok) return revocationSourceUnreachable(audit)
        let jws
        try {
            jws = await resp.text()
        } catch {
            return revocationSourceUnreachable(audit)
        }

        const result = await verifyRevocation(jws, verifyOptions)
        if (!result.valid || result.revokes !== tokenHash) return false

        // Best-effort: a failed write costs a refetch, not a wrong answer.
        await persist(tokenHash, jws)
        return true
    }

    /**
     * Ask the domain about several hashes in one request (a grant's anchor
     * and its leaf), so the isRevoked calls that follow are answered from
     * what this learned. A domain without the batch route (404), or one we
     * can't reach, leaves them to be asked one at a time, as before.
     */
    isRevoked.prefetch = async function prefetch(hashes) {
        const now = Date.now()
        const want = []
        for (const h of new Set(hashes || [])) {
            if (!HEX_64.test(h || '') || clean.get(h) > now) continue
            if (await isRevokedLocally(h, verifyOptions)) continue
            want.push(h)
        }
        if (want.length < 2) return // one hash is one request either way
        let body
        try {
            const resp = await fetch(`${authUrl}/revocations?hashes=${want.join(',')}`)
            if (!resp.ok) return
            body = await resp.json()
        } catch {
            return
        }
        const found = body?.revocations
        if (!found || typeof found !== 'object') return
        for (const h of want) {
            if (!(h in found)) continue
            const jws = found[h]
            if (!jws) { markClean(clean, h); continue }
            // Checked here before it counts, as a single answer is
            const result = await verifyRevocation(jws, verifyOptions)
            if (result.valid && result.revokes === h) await persist(h, jws)
        }
    }

    return isRevoked
}

/**
 * Peer-to-peer delivery of revocations — Phase 3.
 *
 * The object already carries its own proof, so the channel can be dumb and
 * receiving can be open to anyone: a revocation is checked, never trusted, and
 * it is monotone, so a verified one can only reduce access. That is the half of
 * the argument that always worked.
 *
 * **Sending is the constrained half, and the constraint is privacy, not
 * safety.** Nothing in this system enumerates revocations: every public route is
 * keyed by a hash the caller must already know, and the list itself is never
 * served. Broadcasting revocations would be the first way to learn hashes you
 * did not have. So a peer is only ever offered revocations for chains it has
 * *already shown us* — hashes it demonstrably holds, where telling it discloses
 * nothing.
 *
 * That rule is enforced by construction rather than by a filter: the only
 * hashes this ever looks up are ones a peer handed over itself. The store is
 * never scanned, so there is no code path that could leak by accident.
 *
 * It doubles as the flood guard. What a peer may push at us is bounded the same
 * way — by the chains it has shown — so no one can fill this store with valid
 * but irrelevant objects.
 *
 * @param {Object} options
 * @param {string} [options.authUrl]
 * @param {Function} options.resolvePublicKey
 * @param {Function} [options.resolveLink]
 * @param {Function} options.send - (peerId, message) => void
 */
export function createRevocationGossip({
    authUrl = '/auth',
    resolvePublicKey,
    resolveLink,
    send,
} = {}) {
    // peerId -> hashes that peer has demonstrably shown us.
    const shown = new Map()

    const link = resolveLink || createLinkResolver(authUrl)
    // Peer-delivered objects are verified as such: `origin` stays 'peer', so a
    // server key gets no exception and must earn its place in the ancestry.
    const verifyOptions = { resolvePublicKey, resolveLink: link }

    function held(peerId) {
        let set = shown.get(peerId)
        if (!set) shown.set(peerId, (set = new Set()))
        return set
    }

    return {
        /**
         * Record the chain a peer presented. This is the disclosure boundary:
         * whatever is in here, that peer already holds.
         *
         * Hooks onto the moment a peer presents a credential — it has to hand
         * over the chain to be verified at all, so the set costs nothing extra
         * to build.
         */
        async noteChainShown(peerId, leafJwt) {
            if (!peerId || !leafJwt) return []
            const { chain, leaf } = await resolveChain(authUrl, leafJwt, { resolveLink: link })
            if (!chain) return []
            const hashes = []
            for (const jwt of chain) hashes.push(await hashToken(jwt))
            if (leaf) hashes.push(await hashToken(leaf))
            const set = held(peerId)
            for (const h of hashes) set.add(h)
            return hashes
        },

        /** Everything this peer has shown us. Its own hashes, nothing more. */
        shownBy(peerId) {
            return [...(shown.get(peerId) || [])]
        },

        /**
         * Offer a peer every revocation we hold that touches a chain it showed
         * us. Returns how many were sent.
         */
        async shareWith(peerId) {
            let sent = 0
            for (const hash of held(peerId)) {
                const jws = await loadRevocation(hash, verifyOptions)
                if (!jws) continue
                send?.(peerId, { type: 'revocationShare', jws })
                sent++
            }
            return sent
        },

        /**
         * We just learned a hash is revoked. Tell the peers who hold it —
         * and only them.
         */
        async announce(hash) {
            const jws = await loadRevocation(hash, verifyOptions)
            if (!jws) return 0
            let sent = 0
            for (const [peerId, set] of shown) {
                if (!set.has(hash)) continue
                send?.(peerId, { type: 'revocationShare', jws })
                sent++
            }
            return sent
        },

        /**
         * Accept a revocation from a peer. From anyone — the object is checked
         * before it counts, so a hostile or mistaken sender cannot revoke
         * anything by assertion.
         *
         * Kept only if it names a chain that peer showed us. That is the flood
         * guard, and it costs nothing to state because it is the same set the
         * send side already maintains.
         */
        async receive(peerId, message) {
            const jws = message?.jws
            if (!jws) return { stored: false, error: 'No revocation in message' }

            const result = await verifyRevocation(jws, verifyOptions)
            if (!result.valid) return { stored: false, error: result.error }

            if (!held(peerId).has(result.revokes)) {
                return { stored: false, error: 'Revocation is not for a chain this peer showed us' }
            }
            return persist(result.revokes, jws)
        },

        /** Forget a disconnected peer. Nothing here outlives the session. */
        forgetPeer(peerId) {
            shown.delete(peerId)
        },
    }
}
